/**
 * Per-vertical landing pages: /for-storage, /for-multifamily,
 * /for-parking, /for-marina.
 *
 * Single component, content keyed off the `vertical` prop. The
 * marketing thesis is "lead-vertical-per-channel, never 'we do
 * everything'" — when an operator clicks an ad or follows a backlink
 * for their vertical, the page they land on should read entirely in
 * their language (not "and three other verticals").
 */
import { Link } from "react-router-dom";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { breadcrumbList, faqJsonLd, useSeo } from "../../lib/useSeo";
import { HeroSignupCard } from "./HeroSignupCard";
import { PilotPromoSection } from "./PilotPromoSection";

type VerticalKey = "self_storage" | "multifamily" | "parking" | "marina";

interface VerticalContent {
  pillLabel: string;
  pillTone: "accent" | "ok" | "warn";
  headline: string;
  headlineAccent: string;
  subhead: string;
  /** Headline rendered above the inline signup card in the hero —
   * confirms to the prospect that the page is set up for their
   * vertical ("Set up your marina in 60 seconds.") */
  signupHeadline: string;
  metaStats: { label: string; value: string }[];
  unitsNoun: string;       // "units", "apartments", "stalls", "slips"
  unitsNounSingular: string;
  competitorsTitle: string;
  competitorComparison: {
    feature: string;
    competitors: string[];
    nestplot: string;
  }[];
  competitorHeaders: string[];
  painPoints: { title: string; body: string }[];
  features: { icon: "grid" | "wallet" | "chart" | "key" | "lock" | "doc"; title: string; body: string }[];
  tools: { slug: string; name: string; blurb: string }[];
  faq: { q: string; a: string }[];
  seoTitle: string;
  seoDescription: string;
  path: string;
}

const CONTENT: Record<VerticalKey, VerticalContent> = {
  self_storage: {
    pillLabel: "Self-storage operations · all-in-one",
    pillTone: "accent",
    headline: "Run a storage facility,",
    headlineAccent: "not a billing reconciliation.",
    subhead:
      "NestPlot is the all-in-one self-storage operations platform for operators who want to leave storEDGE or SiteLink without trading one walled garden for another. Your tenants' rent settles directly into your bank account through your own Stripe — we never touch the money. PTI, SecurGuard, Noke, INSOMNIAC gate integrations are wired in from day one.",
    signupHeadline: "Set up your storage facility in 60 seconds.",
    metaStats: [
      { label: "Setup fee", value: "$0" },
      { label: "Cut on tenant rent", value: "0%" },
      { label: "Sales call required", value: "None" },
    ],
    unitsNoun: "units",
    unitsNounSingular: "unit",
    competitorsTitle: "vs. storEDGE / SiteLink / Easy Storage Solutions",
    competitorHeaders: ["storEDGE", "SiteLink", "NestPlot"],
    competitorComparison: [
      { feature: "Published price", competitors: ["Contact sales", "Contact sales"], nestplot: "From $59/mo flat" },
      { feature: "Payment processor", competitors: ["Storable Payments", "SL Merchant Services"], nestplot: "Your Stripe · 0% cut" },
      { feature: "Setup fee", competitors: ["$1k–$5k reported", "$500–$1.5k"], nestplot: "$0" },
      { feature: "Gate integrations", competitors: ["Storable ecosystem", "Storable ecosystem"], nestplot: "PTI · SecurGuard · INSOMNIAC · Noke" },
      { feature: "Lien-sale workflow", competitors: ["Manual", "Manual"], nestplot: "Per-state, statute-cited, certified-mail wired" },
      { feature: "QuickBooks Online sync", competitors: ["Add-on", "Add-on"], nestplot: "Included" },
    ],
    painPoints: [
      { title: "Storable owns your payment stack", body: "storEDGE and SiteLink both route every tenant payment through their merchant accounts. You can't price-shop, can't switch processors, and outages on their side stop your collections. NestPlot uses Stripe Connect to your bank — no middleman." },
      { title: "$1k–$5k setup fees gate every quote", body: "Operators report onboarding fees that would buy a year of NestPlot. We have a $0 setup fee and a working demo on the public marketing site." },
      { title: "Lien sales are still spreadsheets", body: "Every state has a different default-notice / lien-notice / sale-eligible cadence. NestPlot generates the timeline, schedules the certified mail through Lob, and exports the audit trail when the sale closes." },
    ],
    features: [
      { icon: "key", title: "PTI · SecurGuard · INSOMNIAC · Noke", body: "Per-facility gate vendor configured at signup. Codes are issued automatically on move-in and revoked only on operator action — never automatically based on payment status (Hard Rule #4)." },
      { icon: "doc", title: "All 50 states + DC lien engine", body: "Statute-cited late-fee caps, grace periods, and default → lien-sale notice windows. Certified mail through Lob with the operator-named-mailing-address, fully audited." },
      { icon: "wallet", title: "Stripe Connect built in", body: "Your tenants' rent settles into your bank account through your own Stripe. We never touch it. No processing margin, no escrow risk, no walled garden." },
      { icon: "chart", title: "Pricing AI · advisory by default", body: "Watches occupancy by size and climate; suggests rate moves within the bounds you set. Stays advisory until you decide to auto-apply." },
    ],
    tools: [
      { slug: "late-fee-calculator", name: "Late-fee calculator", blurb: "The maximum legal fee for your state and rent." },
      { slug: "lien-sale-timeline", name: "Lien-sale timeline", blurb: "Default → lien-notice → sale-eligible dates from last payment." },
      { slug: "occupancy-benchmark", name: "Occupancy benchmark", blurb: "How your facility compares to your market." },
      { slug: "self-storage-rate-finder", name: "Rate finder", blurb: "Ballpark rate by unit size and ZIP." },
    ],
    faq: [
      { q: "How does the storEDGE / SiteLink migration work?", a: "Our team handles the CSV import — tenants, units, balances, rates, gate codes — in a single working session. The lease engine pre-loads your state's statutory clauses, and the gate-vendor handshake happens before we hand over the keys." },
      { q: "Does NestPlot do delinquent collections / auctions?", a: "Yes. Per-state delinquency cadence is wired in. NestPlot schedules the certified-mail notices through Lob, tracks the response window, and exports the audit trail when the sale closes. Operator review at every step — no auto-revoke of gate codes for non-payment." },
      { q: "Multi-LLC accounting?", a: "Each Facility can be assigned to a separate Entity (LLC). Per-entity QuickBooks Online, Xero, or Sage Intacct sync. P&L exports route to the right entity automatically." },
    ],
    seoTitle: "Self-storage software — NestPlot",
    seoDescription:
      "Self-storage operations platform for operators leaving storEDGE / SiteLink / Easy Storage Solutions. Your tenants' rent settles to your bank through your own Stripe. PTI, SecurGuard, INSOMNIAC, Noke gate integrations. All 50 states lien engine. From $59/mo flat — no per-door, no setup fee.",
    path: "/for-storage",
  },
  multifamily: {
    pillLabel: "Multifamily operations · for portfolios of every size",
    pillTone: "accent",
    headline: "Run a building,",
    headlineAccent: "not a portal subscription.",
    subhead:
      "NestPlot is the multifamily operations platform for operators of every size — from a single-door landlord to a nationwide portfolio — who don't want to pay Yardi / AppFolio per-door, and don't want a sales-call gate to even see the product. Application screening, lease signing, rent collection through your own Stripe, gate / smart-lock integrations, and per-entity QuickBooks sync are wired in from day one.",
    signupHeadline: "Set up your building in 60 seconds.",
    metaStats: [
      { label: "Setup fee", value: "$0" },
      { label: "Per-door cost", value: "$0" },
      { label: "Sales call required", value: "None" },
    ],
    unitsNoun: "apartments",
    unitsNounSingular: "apartment",
    competitorsTitle: "vs. AppFolio / Yardi Breeze / Buildium",
    competitorHeaders: ["AppFolio", "Yardi Breeze", "NestPlot"],
    competitorComparison: [
      { feature: "Published price", competitors: ["~$1.49/door + $400 base", "~$1/door + $100 base"], nestplot: "From $59/mo flat · unlimited doors" },
      { feature: "Sales call to see pricing", competitors: ["Yes", "Yes"], nestplot: "No · sign up online" },
      { feature: "Tenant rent processor", competitors: ["AppFolio Payments", "Yardi merchant"], nestplot: "Your Stripe · 0% cut" },
      { feature: "Application screening", competitors: ["Add-on per applicant", "Add-on per applicant"], nestplot: "Included · credit + criminal + income" },
      { feature: "Smart-lock / building access", competitors: ["Smart-home add-on", "PointCentral add-on"], nestplot: "Built-in · per-vendor handshake" },
      { feature: "Multi-LLC accounting", competitors: ["Per-portfolio extra", "Per-portfolio extra"], nestplot: "Included · QuickBooks / Xero / Sage" },
    ],
    painPoints: [
      { title: "Per-door pricing penalizes the wrong thing", body: "A 20-door building shouldn't cost $30+/month before you do anything. NestPlot's flat pricing (from $59/mo) scales with what you need (number of properties), not what you can't control (number of doors)." },
      { title: "Sales-call gate before pricing", body: "Yardi, AppFolio, and DoorLoop all require a demo before they'll quote you. NestPlot's pricing is on the public site and you can sign up without talking to anyone." },
      { title: "Tenant screening lives behind a paywall", body: "AppFolio bills $30-40 per credit + criminal pull. NestPlot includes it in the flat plan — credit, criminal, eviction, and income verification through the same screening partner the enterprise players use." },
    ],
    features: [
      { icon: "doc", title: "Inquire → screen → lease", body: "Public floor-plan listings with per-unit availability. Prospects inquire by plan (apt# is private until lease signing). Screening pulls credit + criminal + income. Lease signing with UETA / E-SIGN audit trail in every state." },
      { icon: "key", title: "Smart-lock + building access", body: "Per-property vendor handshake — Salto, Latch, RemoteLock, August Smart Home. Codes / mobile keys are revoked only on lease-end, never automatically on payment status (Hard Rule #4)." },
      { icon: "wallet", title: "Stripe Connect built in", body: "Rent settles into your bank account through your own Stripe. No per-door processing margin. ACH / card both supported; ACH is free for the operator." },
      { icon: "chart", title: "Concession + pricing AI", body: "Suggests concessions when a unit lists more than 14 days. Per-state rent-cap awareness — California, Oregon, and NY rent-stabilized units are bounded automatically." },
    ],
    tools: [
      { slug: "security-deposit-cap", name: "Security deposit cap", blurb: "The maximum deposit your state lets you collect." },
      { slug: "eviction-timeline-builder", name: "Eviction timeline", blurb: "Per-state notice + court-filing windows." },
      { slug: "rent-stabilization-finder", name: "Rent stabilization finder", blurb: "Is the building under rent stabilization?" },
      { slug: "habitability-checklist", name: "Habitability checklist", blurb: "The warranty-of-habitability items your state requires." },
    ],
    faq: [
      { q: "How does NestPlot handle rent stabilization?", a: "Per-state rent caps wired in. California AB 1482, NY rent stabilization, Oregon SB 608, Maryland HB 1 — the cap is enforced when you set a new lease rate, with an override that requires operator confirmation + a logged reason." },
      { q: "Multi-LLC accounting?", a: "Each property can be assigned to a separate Entity (LLC). Per-entity QuickBooks Online, Xero, or Sage Intacct sync. Distributions, expenses, and rent settlements route to the right entity automatically." },
      { q: "What about HOAs / condo associations?", a: "On the roadmap — currently NestPlot is purpose-built for rental multifamily. If you operate condos / HOA fees, talk to us about the pilot terms." },
    ],
    seoTitle: "Multifamily property management software — NestPlot",
    seoDescription:
      "Multifamily operations platform for operators of every size — a single landlord to the largest managers. From $59/mo flat — no per-door pricing, no sales-call gate. Stripe Connect to your bank · screening / lease signing / smart-lock access wired in. Alternative to AppFolio, Yardi Breeze, Buildium.",
    path: "/for-multifamily",
  },
  parking: {
    pillLabel: "Parking lots + garages · permits + transient",
    pillTone: "accent",
    headline: "Run a parking lot,",
    headlineAccent: "not a permit spreadsheet.",
    subhead:
      "NestPlot is the parking operations platform for owners of private lots, structured garages, and permit-driven facilities who want to escape the per-stall metering apps. Monthly permits, transient gate codes, plate-reader integration, and online permit signup are wired in — no setup fees, no per-stall cost, no sales-call demo.",
    signupHeadline: "Set up your lot in 60 seconds.",
    metaStats: [
      { label: "Setup fee", value: "$0" },
      { label: "Per-stall cost", value: "$0" },
      { label: "Sign up online", value: "Yes" },
    ],
    unitsNoun: "stalls",
    unitsNounSingular: "stall",
    competitorsTitle: "vs. ParkSmart / FlashParking / SpotHero",
    competitorHeaders: ["ParkSmart", "FlashParking", "NestPlot"],
    competitorComparison: [
      { feature: "Published price", competitors: ["Contact sales", "Contact sales"], nestplot: "From $59/mo flat · unlimited stalls" },
      { feature: "Per-transaction fee", competitors: ["Yes (~$0.30)", "Yes"], nestplot: "Stripe-only (~2.9% + 30¢)" },
      { feature: "Gate / LPR integration", competitors: ["Vendor lock", "Vendor lock"], nestplot: "Open · any gate, plate reader, fob" },
      { feature: "Tenant-issued permits", competitors: ["Yes", "Yes"], nestplot: "Online application + auto-issue" },
      { feature: "Monthly + transient combined", competitors: ["Separate products", "Separate products"], nestplot: "Single product" },
    ],
    painPoints: [
      { title: "Per-transaction fees eat your margin", body: "App-based parking apps take a cut on every transient transaction. NestPlot uses Stripe Connect — no platform margin, no per-spot fee." },
      { title: "Permit signup is still email + spreadsheet", body: "Most private-lot operators take permit applications by phone and Excel. NestPlot has online permit signup with auto-approval rules and credit-card-on-file." },
      { title: "Smart gates locked to one vendor", body: "Buying a smart gate from one vendor often locks you to their PMS too. NestPlot integrates with any LPR, plate reader, or RFID — bring your own gate." },
    ],
    features: [
      { icon: "key", title: "Plate reader + RFID + mobile keys", body: "Per-stall access credentials managed in NestPlot. Add a plate, swap a key fob, or pre-authorize a guest — all from the operator dashboard or the permit-holder app." },
      { icon: "doc", title: "Online permit applications", body: "Public permit landing pages by lot type — Garage, Covered, Open, EV. Applicants submit, pay, and get their gate code in one flow. Operator approval optional." },
      { icon: "wallet", title: "Monthly + transient + validations", body: "One platform for monthly permits, transient hourly, validation codes, and event flat-rates. Lobby tablet integration for valet handoff if you run a hybrid lot." },
      { icon: "chart", title: "Pricing by demand", body: "Optional dynamic-pricing for transient spaces. Watches occupancy + day-of-week and suggests rate moves within your operator-set bounds." },
    ],
    tools: [
      { slug: "parking-permit-pricing", name: "Permit pricing benchmark", blurb: "Monthly permit prices by market." },
      { slug: "parking-rate-card-builder", name: "Rate card builder", blurb: "Tiered hourly / daily / monthly rate cards." },
      { slug: "ev-charging-revenue", name: "EV charging revenue", blurb: "Charger payback by utility rate + sessions." },
      { slug: "lot-capacity-calculator", name: "Lot capacity calculator", blurb: "How many stalls fit (per ADA + drive lane)." },
    ],
    faq: [
      { q: "What gate hardware do you support?", a: "We integrate with any LPR, plate reader, RFID reader, mobile-key fob system, or operator-keypad gate. The smart move is bring-your-own-hardware so you keep the leverage — NestPlot handles the permit + payment + access-credential issue." },
      { q: "EV charger integration?", a: "Yes. ChargePoint, EVgo, EV Connect, ABB, and Tritium are integrated. Stripe Connect handles the billing — funds settle to your bank account, not the charger network's." },
      { q: "Hourly + monthly + event combined?", a: "Yes — single product. A permit-holder lot can sell transient hourly to non-permit cars and reserve specific stalls for event flat-rates. Rate cards are per-lot configurable." },
    ],
    seoTitle: "Parking management software — NestPlot",
    seoDescription:
      "Parking operations platform for owners of private lots, structured garages, and permit-driven facilities. Monthly permits + transient + validations in one platform. Stripe Connect to your bank — no per-transaction fee. Open integration with any gate / plate reader. From $59/mo flat.",
    path: "/for-parking",
  },
  marina: {
    pillLabel: "Marinas + dry stack · slip + boater operations",
    pillTone: "accent",
    headline: "Run a marina,",
    headlineAccent: "not Marina Office Manager.",
    subhead:
      "NestPlot is the marina operations platform for marinas, dry stack facilities, and yacht clubs who want a modern system without paying enterprise prices. Slip rentals (by LOA, with hookups), dry-stack scheduling, fuel-dock billing, dock-access credentials, and per-LLC accounting are wired in — no setup fees, no per-slip cost.",
    signupHeadline: "Set up your marina in 60 seconds.",
    metaStats: [
      { label: "Setup fee", value: "$0" },
      { label: "Per-slip cost", value: "$0" },
      { label: "Sign up online", value: "Yes" },
    ],
    unitsNoun: "slips",
    unitsNounSingular: "slip",
    competitorsTitle: "vs. Marina Office Manager / DockMaster / Speedy Dock",
    competitorHeaders: ["Marina Office", "DockMaster", "NestPlot"],
    competitorComparison: [
      { feature: "Published price", competitors: ["Contact sales", "Contact sales"], nestplot: "From $59/mo flat · unlimited slips" },
      { feature: "Web-based", competitors: ["Hybrid", "Server software"], nestplot: "Cloud-only · modern UX" },
      { feature: "Boater rent processor", competitors: ["Vendor merchant", "Vendor merchant"], nestplot: "Your Stripe · 0% cut" },
      { feature: "Dry stack scheduling", competitors: ["Add-on", "Add-on"], nestplot: "Included" },
      { feature: "Per-LLC accounting", competitors: ["Per-facility extra", "Per-facility extra"], nestplot: "Included · QuickBooks / Xero / Sage" },
      { feature: "Setup fee", competitors: ["$2k+", "$1k–$3k"], nestplot: "$0" },
    ],
    painPoints: [
      { title: "Marina software still ships on a desktop", body: "Marina Office Manager is hybrid desktop / cloud. DockMaster runs on a server in the harbor office. NestPlot is cloud-only and works on the same iPhone you carry on the dock." },
      { title: "Per-LOA pricing complexity", body: "Slip rates are usually $X / foot / month, with adjustments for beam, draft, and hookups. NestPlot models this natively — operator sets the per-foot rate; the system calculates the right invoice for each boat." },
      { title: "Dry stack is its own product", body: "Most marina systems make you pay extra for dry-stack scheduling. NestPlot includes it: launch / haul scheduling, weight + LOA validation, and SMS notifications to boaters." },
    ],
    features: [
      { icon: "doc", title: "Slip rentals by LOA + beam + draft", body: "Per-vessel data captured at lease signing. Per-foot rate engine handles the math automatically. Seasonal pricing, hookup add-ons (water / 30A / 50A / WiFi), and pump-out fees all configurable." },
      { icon: "key", title: "Dock-access credentials", body: "Gate code, key fob, or mobile-key — all per-slip and per-boater. Credentials revoke only on operator action (Hard Rule #4 — never automatically on payment status)." },
      { icon: "wallet", title: "Stripe Connect built in", body: "Boater rent + fuel + transient settles into your bank account through your own Stripe. ACH free, card at standard Stripe rates. No processing margin, no merchant lock-in." },
      { icon: "chart", title: "Fuel dock + transient billing", body: "Per-gallon fuel sales, transient slip reservations, and pump-out fees all flow through the same billing. Single statement to the boater on month-end." },
    ],
    tools: [
      { slug: "slip-loa-pricing-calculator", name: "Slip LOA pricing", blurb: "Per-foot pricing for slips by LOA." },
      { slug: "dry-stack-throughput", name: "Dry stack throughput", blurb: "Launches per day given staff + boats." },
      { slug: "marina-occupancy-benchmark", name: "Occupancy benchmark", blurb: "Slip occupancy vs your region." },
      { slug: "winter-storage-pricing", name: "Winter storage pricing", blurb: "Seasonal vs annual rate calculator." },
    ],
    faq: [
      { q: "Do you handle dry stack?", a: "Yes — included, not an add-on. Launch / haul scheduling, weight + LOA validation, and SMS notifications to boaters when their rig is ready. Operators using a forklift fleet can track per-rack availability." },
      { q: "Seasonal vs annual contracts?", a: "Both. Per-slip seasonal pricing (e.g. $X/ft/mo May-Oct) or full-year contracts. Auto-renewal with operator-configured opt-out window. NY operators get the stronger UETA / ESRA signing identity-binding by default." },
      { q: "Fuel dock?", a: "Per-gallon fuel sales billed through the same Stripe Connect rail. The boater gets one statement on month-end with slip + fuel + pump-out + transient. Reconciles to QuickBooks per-entity (single LLC or multi)." },
    ],
    seoTitle: "Marina management software — NestPlot",
    seoDescription:
      "Marina operations platform for marinas, dry stack facilities, and yacht clubs. Slip rentals by LOA · dry-stack scheduling · fuel-dock billing · dock-access credentials · per-LLC accounting. Cloud-only, no desktop server. Stripe Connect to your bank. From $59/mo flat — no per-slip cost.",
    path: "/for-marina",
  },
};

// URL-slug conventions across NestPlot routes (see App.tsx + audit
// P0). Each vertical maps to a different slug per route family:
//   /verticals/{kebab}   /pilot/{short}   /vs/vertical/{underscore}   /tools/vertical/{underscore}
// The "displayName" is what shows in breadcrumbs + related-pages
// cards (the CONTENT map above is heavier per-page copy; keeping the
// short name here avoids overlap).
const CROSS_LINKS: Record<VerticalKey, {
  displayName: string;
  verticals: string;
  pilot: string;
  vs: string;
  tools: string;
}> = {
  multifamily: {
    displayName: "Multifamily",
    verticals: "multifamily", pilot: "multifamily",
    vs: "multifamily", tools: "multifamily",
  },
  self_storage: {
    displayName: "Self-storage",
    verticals: "self-storage", pilot: "storage",
    vs: "self_storage", tools: "self_storage",
  },
  parking: {
    displayName: "Parking",
    verticals: "parking", pilot: "parking",
    vs: "parking", tools: "parking",
  },
  marina: {
    displayName: "Marina + RV",
    verticals: "rv-and-boat", pilot: "marina",
    vs: "vehicle_storage", tools: "vehicle_storage",
  },
};

export function ForVerticalPage({ vertical }: { vertical: VerticalKey }) {
  const c = CONTENT[vertical];
  const links = CROSS_LINKS[vertical];
  useSeo({
    title: c.seoTitle,
    description: c.seoDescription,
    path: c.path,
    // Emit FAQ schema for the FAQ block at the bottom of the page
    // (SEO audit P1 — was missing on all 4 /for-{vertical} pages).
    jsonLd: [
      ...(c.faq.length > 0 ? [faqJsonLd(c.faq)] : []),
      breadcrumbList([
        { name: "Home", path: "/" },
        { name: links.displayName, path: c.path },
      ]),
    ],
  });

  return (
    <div>
      {/* Hero — two-column on desktop: copy on the left, signup card
          on the right so prospects can start their trial without
          leaving the vertical-native landing page. Stacks on mobile. */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 lg:py-16 grid lg:grid-cols-[1.3fr_1fr] gap-10 items-start">
          <div>
            <Pill tone={c.pillTone} size="sm">
              {c.pillLabel}
            </Pill>
            <h1 className="t-display-xl mt-5">
              {c.headline}
              <br />
              <span className="text-accent">{c.headlineAccent}</span>
            </h1>
            <p className="text-lg text-sub mt-5 max-w-[560px]">{c.subhead}</p>
            {/* An operator arriving from a paid search costs us $2–5 a click
                and the whole national market is about 1,790 searches a month,
                so the first screen has to ask for something. It used to offer
                only a ghost link to pricing. What this visitor actually wants
                is to know whether it fits their site, and fifteen minutes with
                a person answers that faster than a trial they have to set up
                alone — which is why 64 clicks produced no sign-ups. */}
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/book-demo">
                <Btn kind="primary" size="md" iconRight={<Icon name="arrow-right" size={14} />}>
                  Book a 15-minute walkthrough
                </Btn>
              </Link>
              <Link to="/pricing">
                <Btn kind="ghost" size="md">
                  See pricing →
                </Btn>
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap gap-8 text-sm">
              {c.metaStats.map((s) => (
                <div key={s.label}>
                  <div className="font-mono text-[10px] text-sub uppercase tracking-[0.14em]">
                    {s.label}
                  </div>
                  <div className="font-display font-black text-[22px] tracking-[-0.02em]">
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <HeroSignupCard
              contextLine={c.signupHeadline}
              // Pre-pick the vertical on /signup step 2 so the
              // prospect doesn't restate it after the page already
              // established it. multifamily / self_storage / parking
              // map 1:1; marina maps to vehicle_storage (the closest
              // first-class facility_type on the signup picker).
              facilityType={
                vertical === "marina" ? "vehicle_storage" : vertical
              }
            />
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="border-b border-line bg-bg">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            What we hear
          </div>
          <h2 className="t-display-l mt-2 max-w-[640px]">
            The three things {c.unitsNoun.replace(/s$/, "")}-operators tell us first.
          </h2>
          <div className="grid md:grid-cols-3 gap-4 mt-8">
            {c.painPoints.map((p) => (
              <Card key={p.title}>
                <div className="px-5 py-5">
                  <h3 className="font-display font-extrabold text-[16px] uppercase tracking-[-0.005em]">
                    {p.title}
                  </h3>
                  <p className="text-sub text-[13.5px] mt-2 leading-relaxed">{p.body}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-b border-line bg-surface">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 grid md:grid-cols-2 gap-6">
          {c.features.map((f) => (
            <div key={f.title} className="flex items-start gap-4">
              <div className="bg-accent rounded-md p-3 text-on-accent shrink-0">
                <Icon name={f.icon} size={20} />
              </div>
              <div>
                <h3 className="font-display font-extrabold text-[16px] uppercase tracking-[-0.005em]">
                  {f.title}
                </h3>
                <p className="text-sub text-[13.5px] mt-2 leading-relaxed">{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Competitor comparison */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Comparison
          </div>
          <h2 className="t-display-l mt-2">{c.competitorsTitle}</h2>
          <div className="mt-8 overflow-x-auto">
            <table className="w-full border border-line rounded-lg bg-paper text-sm overflow-hidden">
              <thead>
                <tr className="bg-surface text-sub font-mono text-[11px] tracking-[0.08em] uppercase">
                  <th className="text-left px-5 py-3 font-medium">Feature</th>
                  {c.competitorHeaders.slice(0, -1).map((h) => (
                    <th key={h} className="text-left px-5 py-3 font-medium">
                      {h}
                    </th>
                  ))}
                  <th className="text-left px-5 py-3 font-medium">
                    <span className="text-accent">NestPlot</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {c.competitorComparison.map((row) => (
                  <tr key={row.feature} className="border-t border-line">
                    <td className="px-5 py-3 font-semibold">{row.feature}</td>
                    {row.competitors.map((v, i) => (
                      <td key={i} className="px-5 py-3 text-sub">
                        {v}
                      </td>
                    ))}
                    <td className="px-5 py-3 text-accent font-semibold">{row.nestplot}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <PilotPromoSection />

      {/* Free tools */}
      <section className="border-b border-line bg-bg">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            Free tools
          </div>
          <h2 className="t-display-l mt-2">No signup. Just answers.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {c.tools.map((t) => (
              <Link key={t.slug} to={`/tools/${t.slug}`} className="block group">
                <Card className="h-full hover:shadow-rollup transition-shadow">
                  <div className="px-5 py-4">
                    <h3 className="font-display font-extrabold text-[15px] uppercase tracking-[-0.005em]">
                      {t.name}
                    </h3>
                    <p className="text-sub text-[12.5px] mt-2">{t.blurb}</p>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-line">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12">
          <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
            FAQ
          </div>
          <h2 className="t-display-l mt-2">Questions we hear</h2>
          <div className="mt-6 space-y-3 max-w-[860px]">
            {c.faq.map(({ q, a }) => (
              <details
                key={q}
                className="bg-paper border border-line rounded-md px-5 py-4"
              >
                <summary className="cursor-pointer font-semibold text-[15px]">
                  {q}
                </summary>
                <p className="text-sub text-[14px] leading-[1.6] mt-3">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section>
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-14 text-center">
          <h2 className="font-display font-black text-[32px] lg:text-[40px] tracking-[-0.02em]">
            Try NestPlot on your {c.unitsNounSingular}.
          </h2>
          <p className="text-sub max-w-[560px] mx-auto mt-3">
            Migration is included on every plan — no setup fee, the team runs
            it with you. Start the 30-day free trial, no card required.
          </p>
          <div className="mt-6 inline-flex gap-3 flex-wrap justify-center">
            <Link to="/book-demo">
              <Btn kind="primary" size="lg" iconRight={<Icon name="arrow-right" size={14} />}>
                Book a 15-minute walkthrough
              </Btn>
            </Link>
            <Link to={`/pilot/${links.pilot}`}>
              <Btn kind="secondary" size="lg">
                Plan my migration
              </Btn>
            </Link>
            <Link to="/signup">
              <Btn kind="ghost" size="lg">
                Start free · 30 days
              </Btn>
            </Link>
          </div>
        </div>
      </section>

      {/* Related pages — cross-links to the vertical hub, competitor
          comparisons, and free tools. Fixes the "orphaned sibling
          verticals" gap flagged in the SEO audit (P0). */}
      <section className="mt-10 px-5 lg:px-8 max-w-[1200px] mx-auto">
        <div className="border-t border-line pt-8">
          <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-sub mb-3">
            More for {links.displayName.toLowerCase()} operators
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Link
              to={`/verticals/${links.verticals}`}
              className="border border-line rounded-md px-4 py-3 hover:bg-paper hover:border-line-strong transition-colors"
            >
              <div className="font-semibold text-[14px]">
                {links.displayName} product overview
              </div>
              <div className="text-sub text-[12px] mt-1">
                End-to-end platform + integrations for this vertical
              </div>
            </Link>
            <Link
              to={`/vs/vertical/${links.vs}`}
              className="border border-line rounded-md px-4 py-3 hover:bg-paper hover:border-line-strong transition-colors"
            >
              <div className="font-semibold text-[14px]">vs Competitors</div>
              <div className="text-sub text-[12px] mt-1">
                Head-to-head comparisons filtered for {links.displayName.toLowerCase()}
              </div>
            </Link>
            <Link
              to={`/tools/vertical/${links.tools}`}
              className="border border-line rounded-md px-4 py-3 hover:bg-paper hover:border-line-strong transition-colors"
            >
              <div className="font-semibold text-[14px]">Free tools</div>
              <div className="text-sub text-[12px] mt-1">
                Calculators + state lookups for {links.displayName.toLowerCase()}
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
